import { CircleCheck, ClipboardCheck, Keyboard, Moon, RotateCcw, Sun, X } from "lucide-react";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";

import { MediaPlayerHandle } from "@/components/MediaPlayer";
import { Button, confirmDialog, Dialog, Kbd, KbdGroup } from "@/components/ui";
import { useInvalidateVideoProjectEventLogs } from "@/containers/Editor/VideoProjectPage/components/VideoProjectHistoryPanel";
import { cn } from "@/lib/utils";
import { EDITOR_VIDEO_PROJECT_EXTRA_FIELDS, VideoProject, VideoProjectServices } from "@/services/bedrock";
import { editorQueueListUrl, getErrorMessage, useComplianceChecker, useSocialCoachSession } from "@/utils";
import { StylePreferencesOptions } from "@/utils/projectHelpers";

import { QaAuditPicker } from "./QaAuditPicker";
import { QaContextRail } from "./QaContextRail";
import { QaFlaggedPanel } from "./QaFlaggedPanel";
import { QaLenses } from "./QaLenses";
import { QaPlayerStage } from "./QaPlayerStage";
import {
  buildQaIssues,
  clampQaTimestampMs,
  createQaFlagId,
  flagQaIssueNavKey,
  focusQaIssueCard,
  lastFailComment,
  LastFailResolution,
  neighborAfterRemove,
  neighborQaIssueKey,
  parseQaIssueNavKey,
  QaFlag,
  qaIssueNavKeys,
  selectedQaIssueNavKey,
  timestampMsFromTimelineX,
  unmarkedLastFailIssues,
  unresolvedLastFailIssues,
} from "./qaIssues";
import {
  categoryFromShortcut,
  clampPlayheadSeconds,
  clipForQaIssue,
  isTypingTarget,
  isWatchComplete,
  CURRENT_QA_AUDIT_KEY,
  findQaAudit,
  lastRejectedAudit,
  LENS_SPOTLIGHT,
  qaAuditSelectItems,
  qaStudioClips,
  qaStudioThemeClassName,
  QaStudioTheme,
  QaStudioClip,
  readQaStudioTheme,
  writeQaStudioTheme,
  playbackRateFromShortcut,
  PlaybackRate,
  QA_SKIP_SECONDS,
  stackSkipCueSeconds,
  hasTranscriptPollChange,
  shouldPollEditedSpokenTranscripts,
  shouldShowTranscriptRail,
  seekSecondsForTimedLine,
  SpotlightRegion,
  TimedLine,
} from "./qaStudio";
import { ShortcutsOverlay } from "./ShortcutsOverlay";

const EMPTY_QA_AUDIT_ISSUES: VideoProject.QaAuditIssue[] = [];

interface VideoProjectQaStudioProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoProject: VideoProject;
  setVideoProject: React.Dispatch<React.SetStateAction<VideoProject | undefined>>;
  playerNotes?: VideoProject.ClientNote;
  coachNotes?: VideoProject.ClientNote;
  preferencesInfo: StylePreferencesOptions[];
  formatHexColor: (color: string) => string | null;
  videoProjectMessages?: VideoProject.Message[];
  finalMessage: string;
  hashtags: string;
  readOnly?: boolean;
}

export function VideoProjectQaStudio({
  open,
  onOpenChange,
  videoProject,
  setVideoProject,
  playerNotes,
  coachNotes,
  preferencesInfo,
  formatHexColor,
  videoProjectMessages,
  finalMessage,
  hashtags,
  readOnly = false,
}: VideoProjectQaStudioProps) {
  const history = useHistory();
  const { userId } = useSocialCoachSession();
  const invalidateEventLogs = useInvalidateVideoProjectEventLogs();
  const [theme, setTheme] = React.useState<QaStudioTheme>(() => readQaStudioTheme(userId));

  React.useEffect(() => {
    setTheme(readQaStudioTheme(userId));
  }, [userId]);

  const themeClassName = qaStudioThemeClassName(theme);
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    writeQaStudioTheme(next, userId);
  };
  const playerRef = React.useRef<MediaPlayerHandle>(null);
  const pendingSeekRef = React.useRef<number | null>(null);
  const timelineRef = React.useRef<HTMLDivElement>(null);
  const onStudioKeyDownRef = React.useRef<(event: KeyboardEvent) => void>(() => undefined);
  const isMac = React.useMemo(() => {
    if (typeof window === "undefined") return true;
    return /Macintosh|Mac OS X/i.test(navigator.userAgent);
  }, []);
  const modKey = isMac ? "⌘" : "Ctrl";

  const [playbackRate, setPlaybackRate] = React.useState<PlaybackRate>(1);
  const [playing, setPlaying] = React.useState(false);
  const [currentSeconds, setCurrentSeconds] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [maxPlayed, setMaxPlayed] = React.useState(0);
  const [skippedWatch, setSkippedWatch] = React.useState(false);
  const [selectedClipKey, setSelectedClipKey] = React.useState<string | null>(null);
  const [selectedAuditKey, setSelectedAuditKey] = React.useState(CURRENT_QA_AUDIT_KEY);
  const [flags, setFlags] = React.useState<QaFlag[]>([]);
  const [lastFailResolutions, setLastFailResolutions] = React.useState<Record<number, LastFailResolution | undefined>>(
    {}
  );
  const [lastFailTimestamps, setLastFailTimestamps] = React.useState<Record<number, number | null | undefined>>({});
  const [lastFailComments, setLastFailComments] = React.useState<Record<number, string | undefined>>({});
  const [selectedFlagId, setSelectedFlagId] = React.useState<string | null>(null);
  const [selectedLastFailId, setSelectedLastFailId] = React.useState<number | null>(null);
  const [autoFocusComment, setAutoFocusComment] = React.useState(false);
  const [highlightUnmarkedLastFail, setHighlightUnmarkedLastFail] = React.useState(false);
  const firstUnmarkedLastFailRef = React.useRef<HTMLDivElement>(null);
  const [spotlight, setSpotlight] = React.useState<VideoProject.QaIssueCategory | null>(null);
  const [showShortcuts, setShowShortcuts] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [playbackCue, setPlaybackCue] = React.useState<{ id: number; playing: boolean } | null>(null);
  const [skipCue, setSkipCue] = React.useState<{ id: number; at: number; seconds: number } | null>(null);
  const [transcriptCollapsed, setTranscriptCollapsed] = React.useState(false);

  const setPlayingWithCue = React.useCallback((next: boolean | ((current: boolean) => boolean)) => {
    setPlaying(current => {
      const resolved = typeof next === "function" ? next(current) : next;
      if (resolved !== current) {
        setPlaybackCue({ id: Date.now(), playing: resolved });
      }
      return resolved;
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setFlags([]);
    setLastFailResolutions({});
    setLastFailTimestamps({});
    setLastFailComments({});
    setSelectedFlagId(null);
    setSelectedLastFailId(null);
    setAutoFocusComment(false);
    setHighlightUnmarkedLastFail(false);
    setSpotlight(null);
    setSelectedClipKey(null);
    const auditOptions = qaAuditSelectItems(videoProject.qaAudits, videoProject.status);
    setSelectedAuditKey(
      auditOptions.find(item => item.latest)?.value ?? auditOptions[0]?.value ?? CURRENT_QA_AUDIT_KEY
    );
    setSkippedWatch(false);
    setMaxPlayed(0);
    setCurrentSeconds(0);
    setPlaying(false);
    setShowShortcuts(false);
    setPlaybackCue(null);
    setSkipCue(null);
    setTranscriptCollapsed(false);
    pendingSeekRef.current = null;
  }, [open, videoProject.id]);

  React.useEffect(() => {
    setSelectedClipKey(null);
    setCurrentSeconds(0);
    setPlaying(false);
    setSelectedFlagId(null);
    setSelectedLastFailId(null);
    setAutoFocusComment(false);
  }, [selectedAuditKey]);

  const selectedAudit = findQaAudit(videoProject.qaAudits, selectedAuditKey);
  const viewingPast = Boolean(selectedAudit);
  const studioReadOnly = readOnly || viewingPast;
  const priorTimestampOverrides = viewingPast ? {} : lastFailTimestamps;
  const priorCommentOverrides = viewingPast ? {} : lastFailComments;
  const playheadTimestampMs = Math.round(currentSeconds * 1000);

  const selectFlag = (id: string | null, options?: { editComment?: boolean }) => {
    setSelectedFlagId(id);
    setSelectedLastFailId(null);
    setAutoFocusComment(Boolean(options?.editComment));
  };
  const selectLastFail = (id: number | null, options?: { editComment?: boolean }) => {
    setSelectedLastFailId(id);
    setSelectedFlagId(null);
    setAutoFocusComment(Boolean(options?.editComment));
  };
  const selectIssueByNavKey = (key: string, options?: { editComment?: boolean }) => {
    const parsed = parseQaIssueNavKey(key);
    if (parsed?.kind === "flag") selectFlag(parsed.id, options);
    else if (parsed?.kind === "last-fail") selectLastFail(parsed.id, options);
  };
  const clips = React.useMemo(
    () =>
      qaStudioClips(viewingPast ? selectedAudit?.editedMedia : videoProject.editedMedia, videoProject.originalMedia),
    [selectedAudit?.editedMedia, videoProject.editedMedia, videoProject.originalMedia, viewingPast]
  );
  const lastFail = viewingPast ? undefined : lastRejectedAudit(videoProject.qaAudits);
  const lastFailIssues = lastFail?.qaAuditIssues ?? EMPTY_QA_AUDIT_ISSUES;
  const displayedPriorIssues = viewingPast ? selectedAudit?.qaAuditIssues ?? EMPTY_QA_AUDIT_ISSUES : lastFailIssues;
  const selectedClip =
    clips.find(clip => clip.key === selectedClipKey) ?? clips.find(clip => clip.source === "EDITED") ?? clips[0];
  const activeMedia = selectedClip?.media;
  const canFlag = !studioReadOnly && selectedClip?.source === "EDITED";
  const qaAuditOptions = React.useMemo(
    () => qaAuditSelectItems(videoProject.qaAudits, videoProject.status),
    [videoProject.qaAudits, videoProject.status]
  );

  React.useEffect(() => {
    if (!qaAuditOptions.length) return;
    if (qaAuditOptions.some(item => item.value === selectedAuditKey)) return;
    setSelectedAuditKey(qaAuditOptions[0].value);
  }, [qaAuditOptions, selectedAuditKey]);
  const missingPastVideo = viewingPast && !selectedAudit?.editedMedia?.length;
  const { findBlockedWords, findFlaggedWords } = useComplianceChecker({
    playerId: videoProject.playerId,
    coachId: videoProject.coachId,
  });
  const caption = [finalMessage, hashtags].filter(Boolean).join(" ");
  const captionBlockedWords = React.useMemo(
    () => [...new Set([...findBlockedWords(caption), ...findFlaggedWords(caption)])],
    [caption, findBlockedWords, findFlaggedWords]
  );
  const shouldPollSpoken = !viewingPast && shouldPollEditedSpokenTranscripts(videoProject.editedMedia);
  const issues = buildQaIssues(flags);
  const unmarkedLastFail = unmarkedLastFailIssues(lastFailIssues, lastFailResolutions);
  const unresolvedLastFail = unresolvedLastFailIssues(
    lastFailIssues,
    lastFailResolutions,
    lastFailTimestamps,
    lastFailComments
  );
  const hasIssues = issues.length > 0 || unresolvedLastFail.length > 0;
  const watchComplete = isWatchComplete(maxPlayed, skippedWatch);
  const player = videoProject.player;
  const playerName = player?.fullName || videoProject.playerName;
  const playerAliases = player?.aliases?.filter(Boolean) ?? [];
  const regionsAndBranches = [...(player?.regionNames || []), ...(player?.branchNames || [])].filter(Boolean);
  const hasPlayerInfo = Boolean(
    playerName ||
      playerAliases.length ||
      videoProject.companyName ||
      player?.phone ||
      player?.email ||
      player?.webAddress ||
      regionsAndBranches.length
  );

  const mergeTranscriptMedia = React.useCallback(
    (latest: VideoProject) => {
      setVideoProject(prev => {
        if (!prev || !hasTranscriptPollChange(prev, latest)) return prev;
        return {
          ...prev,
          editedMedia: latest.editedMedia ?? prev.editedMedia,
        };
      });
    },
    [setVideoProject]
  );

  React.useEffect(() => {
    if (!open || !shouldPollSpoken) return;

    let cancelled = false;
    const poll = async () => {
      const latest = await VideoProjectServices.show(videoProject.id, EDITOR_VIDEO_PROJECT_EXTRA_FIELDS).catch(
        () => undefined
      );
      if (!cancelled && latest) mergeTranscriptMedia(latest);
    };

    void poll();
    const interval = window.setInterval(() => {
      void poll();
    }, 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [mergeTranscriptMedia, open, shouldPollSpoken, videoProject.id]);

  const applyUpdatedProject = (updated: VideoProject) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        status: updated.status ?? prev.status,
        editorUserId: updated.editorUserId ?? prev.editorUserId,
        qaUserId: updated.qaUserId ?? prev.qaUserId,
        qaStatus: updated.qaStatus ?? prev.qaStatus,
        qaDate: updated.qaDate ?? prev.qaDate,
        qaFailureCount: updated.qaFailureCount ?? prev.qaFailureCount,
        qaAudits: updated.qaAudits ?? prev.qaAudits,
      };
    });
    invalidateEventLogs(videoProject.id);
  };

  const runEvent = async (event: VideoProject.Event, nextIssues?: VideoProject.QaIssue[]) => {
    setSaving(true);
    try {
      const updated = await VideoProjectServices.update(videoProject.id, {
        event,
        ...(nextIssues ? { issues: nextIssues } : {}),
      });
      applyUpdatedProject(updated);
      return updated;
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to update project");
      return undefined;
    } finally {
      setSaving(false);
    }
  };

  const seekTo = React.useCallback(
    (seconds: number) => {
      const next = clampPlayheadSeconds(seconds, duration || activeMedia?.duration || 0);
      playerRef.current?.seekTo(next);
      setCurrentSeconds(next);
    },
    [duration, activeMedia?.duration]
  );

  const seekToTimedLine = React.useCallback(
    (line: TimedLine) => {
      seekTo(seekSecondsForTimedLine(line));
    },
    [seekTo]
  );

  const toggleTranscript = React.useCallback(() => {
    setTranscriptCollapsed(current => !current);
  }, []);

  const skipBy = React.useCallback(
    (requestedDelta: number) => {
      const durationSeconds = duration || activeMedia?.duration || 0;
      const next = clampPlayheadSeconds(currentSeconds + requestedDelta, durationSeconds);
      const actualDelta = next - currentSeconds;
      seekTo(next);
      setSkipCue(current => {
        const now = Date.now();
        const seconds = stackSkipCueSeconds(current, actualDelta, now, requestedDelta);
        if (seconds == null) return current;
        return { id: now, at: now, seconds };
      });
    },
    [activeMedia?.duration, currentSeconds, duration, seekTo]
  );

  const selectClip = React.useCallback((clip: QaStudioClip, seekSeconds?: number) => {
    pendingSeekRef.current = seekSeconds ?? null;
    setSelectedClipKey(clip.key);
    setCurrentSeconds(seekSeconds ?? 0);
    setDuration(0);
    setPlaying(false);
  }, []);

  const handleDuration = React.useCallback((nextDuration: number) => {
    setDuration(nextDuration);
    const pending = pendingSeekRef.current;
    if (pending == null) return;
    pendingSeekRef.current = null;
    const next = clampPlayheadSeconds(pending, nextDuration);
    playerRef.current?.seekTo(next);
    setCurrentSeconds(next);
  }, []);

  const focusIssue = React.useCallback(
    (issue: { timestampMs?: number | null }) => {
      const clip = clipForQaIssue(clips);
      const seekSeconds = issue.timestampMs != null ? issue.timestampMs / 1000 : undefined;
      if (clip && clip.key !== selectedClip?.key) {
        selectClip(clip, seekSeconds);
        return;
      }
      if (seekSeconds != null) seekTo(seekSeconds);
    },
    [clips, selectClip, seekTo, selectedClip?.key]
  );

  const addFlag = React.useCallback(
    (category: VideoProject.QaIssueCategory) => {
      if (!canFlag) return;
      const flag: QaFlag = {
        id: createQaFlagId(),
        category,
        comment: "",
        timestampMs: playheadTimestampMs,
      };
      setFlags(current => [...current, flag]);
      selectFlag(flag.id, { editComment: true });
    },
    [canFlag, playheadTimestampMs]
  );

  const removeFlag = (id: string) => {
    const listed = qaIssueNavKeys(displayedPriorIssues, flags, priorTimestampOverrides);
    const nextKey = neighborAfterRemove(listed, listed.indexOf(flagQaIssueNavKey(id)));
    setFlags(current => current.filter(flag => flag.id !== id));
    if (!nextKey) {
      selectFlag(null);
      return;
    }
    selectIssueByNavKey(nextKey);
    window.setTimeout(() => focusQaIssueCard(nextKey), 0);
  };

  const handleSubmit = async () => {
    if (studioReadOnly || saving) return;
    if (unmarkedLastFail.length) {
      setHighlightUnmarkedLastFail(true);
      toast.error("Mark each previous QA issue as resolved or not.");
      window.requestAnimationFrame(() => {
        firstUnmarkedLastFailRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
      return;
    }
    const emptyFlag = flags.find(flag => !flag.comment.trim());
    const emptyLastFail = emptyFlag
      ? undefined
      : lastFailIssues.find(
          issue =>
            lastFailResolutions[issue.id] === "UNRESOLVED" && !lastFailComment(issue, priorCommentOverrides).trim()
        );
    if (emptyFlag || emptyLastFail) {
      toast.error("Add a comment to each flagged issue.");
      if (emptyFlag) selectFlag(emptyFlag.id, { editComment: true });
      else if (emptyLastFail) selectLastFail(emptyLastFail.id, { editComment: true });
      return;
    }
    const sendingBack = hasIssues;
    const nextIssues = [...issues, ...unresolvedLastFail];
    const watchedPercent = Math.round(maxPlayed * 100);
    const confirmed = await confirmDialog({
      className: themeClassName,
      title: sendingBack ? "Send back for edits?" : "Send to client?",
      description: (
        <div className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
          <p>
            {sendingBack
              ? `This will send ${nextIssues.length} issue${nextIssues.length === 1 ? "" : "s"} back to the editor${
                  unresolvedLastFail.length
                    ? `, including ${unresolvedLastFail.length} still unresolved from the last review`
                    : ""
                }.`
              : "This will mark QA as approved and send the project to the client."}
          </p>
          {!watchComplete ? <p>You have not finished watching the cut ({watchedPercent}% watched).</p> : null}
        </div>
      ),
      confirmLabel: sendingBack ? "Send back for edits" : "Send to client",
      cancelLabel: "Keep reviewing",
      destructive: sendingBack,
    });
    if (!confirmed) return;
    if (!watchComplete) setSkippedWatch(true);

    const updated = await runEvent(
      sendingBack ? VideoProject.Event.REJECT_QA : VideoProject.Event.APPROVE_QA,
      sendingBack ? nextIssues : undefined
    );
    if (!updated) return;
    toast.success(sendingBack ? "Project sent back for edits" : "Project sent to the client");
    onOpenChange(false);
    const queuePath = editorQueueListUrl();
    if (history.location.pathname !== queuePath) {
      history.replace(queuePath);
    }
  };

  onStudioKeyDownRef.current = (event: KeyboardEvent) => {
    const typing = isTypingTarget(event.target);
    if (!typing && (event.key === "?" || (event.key === "/" && event.shiftKey))) {
      event.preventDefault();
      setShowShortcuts(current => !current);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (showShortcuts) {
        setShowShortcuts(false);
        return;
      }
      if (selectedFlagId && typing) {
        (event.target as HTMLElement).blur();
        return;
      }
      if (selectedFlagId) {
        const flag = flags.find(item => item.id === selectedFlagId);
        if (flag && !flag.comment.trim()) removeFlag(flag.id);
        else {
          selectFlag(null);
          (document.activeElement as HTMLElement | null)?.blur();
        }
        return;
      }
      if (selectedLastFailId != null) {
        selectLastFail(null);
        (document.activeElement as HTMLElement | null)?.blur();
        return;
      }
      return;
    }
    const withMod = isMac ? event.metaKey : event.ctrlKey;
    if (withMod && event.key === "Enter") {
      if (studioReadOnly) return;
      event.preventDefault();
      void handleSubmit();
      return;
    }
    if ((event.key === "Backspace" || event.key === "Delete") && selectedFlagId) {
      if (studioReadOnly || typing) return;
      event.preventDefault();
      removeFlag(selectedFlagId);
      return;
    }
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      const listed = qaIssueNavKeys(displayedPriorIssues, viewingPast ? [] : flags, priorTimestampOverrides);
      const nextKey = neighborQaIssueKey(
        listed,
        selectedQaIssueNavKey(selectedFlagId, selectedLastFailId),
        event.key === "ArrowDown" ? 1 : -1
      );
      if (!nextKey) return;
      event.preventDefault();
      event.stopPropagation();
      selectIssueByNavKey(nextKey);
      window.requestAnimationFrame(() => focusQaIssueCard(nextKey));
      return;
    }
    if (typing) return;

    if (event.key === " " || event.key === "k" || event.key === "K") {
      event.preventDefault();
      setPlayingWithCue(current => !current);
      return;
    }
    if (event.key === "j" || event.key === "J" || event.key === "ArrowLeft") {
      event.preventDefault();
      skipBy(-QA_SKIP_SECONDS);
      return;
    }
    if (event.key === "l" || event.key === "L" || event.key === "ArrowRight") {
      event.preventDefault();
      skipBy(QA_SKIP_SECONDS);
      return;
    }
    if (!event.metaKey && !event.ctrlKey && !event.altKey) {
      if (event.key === "t" || event.key === "T") {
        if (!shouldShowTranscriptRail(activeMedia, selectedClip?.source)) return;
        event.preventDefault();
        toggleTranscript();
        return;
      }
      const rate = playbackRateFromShortcut(event.key, playbackRate);
      if (rate != null) {
        event.preventDefault();
        setPlaybackRate(rate);
        return;
      }
    }
    const category = categoryFromShortcut(event.key);
    if (category && canFlag && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey) {
      event.preventDefault();
      addFlag(category);
    }
  };

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => onStudioKeyDownRef.current(event);
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("[data-qa-flag-card], [data-qa-flag-marker]")) return;
      selectFlag(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const focusedIssueCategory =
    flags.find(flag => flag.id === selectedFlagId)?.category ??
    displayedPriorIssues.find(issue => issue.id === selectedLastFailId)?.category ??
    null;
  const activeSpotlight = spotlight ?? focusedIssueCategory;
  const spotlightRegions = activeSpotlight ? LENS_SPOTLIGHT[activeSpotlight] : [];
  const isSpotlighted = (region: SpotlightRegion) => spotlightRegions.includes(region);
  const durationSeconds = duration || activeMedia?.duration || 0;

  const updateFlagTimestamp = React.useCallback(
    (id: string, timestampMs: number | null, seek = true) => {
      const nextMs = timestampMs == null ? null : clampQaTimestampMs(timestampMs, durationSeconds);
      setFlags(current => current.map(item => (item.id === id ? { ...item, timestampMs: nextMs } : item)));
      if (seek && nextMs != null) seekTo(nextMs / 1000);
    },
    [durationSeconds, seekTo]
  );

  const updateLastFailTimestamp = React.useCallback(
    (id: number, timestampMs: number | null, seek = true) => {
      const nextMs = timestampMs == null ? null : clampQaTimestampMs(timestampMs, durationSeconds);
      setLastFailTimestamps(current => ({ ...current, [id]: nextMs }));
      if (seek && nextMs != null) seekTo(nextMs / 1000);
    },
    [durationSeconds, seekTo]
  );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="full"
      disableEscapeDismissal
      className={cn(
        themeClassName,
        "flex h-[calc(100vh-2rem)] max-h-[100vh] min-h-0 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl"
      )}
    >
      <Dialog.Header className="shrink-0 px-5 py-3">
        <div className="flex h-14 min-w-0 items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500 dark:bg-indigo-500/15 dark:text-indigo-300">
            <ClipboardCheck className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <Dialog.Title className="text-base font-bold text-slate-800 dark:text-slate-100">QA Studio</Dialog.Title>
            <p className="truncate text-sm font-medium text-slate-500 dark:text-slate-400">
              {videoProject.playerName || "Player"} (Video Project #{videoProject.id})
            </p>
          </div>
          {qaAuditOptions.length ? (
            <QaAuditPicker
              items={qaAuditOptions}
              value={selectedAudit ? selectedAuditKey : CURRENT_QA_AUDIT_KEY}
              onValueChange={value => setSelectedAuditKey(value || CURRENT_QA_AUDIT_KEY)}
              themeClassName={themeClassName}
            />
          ) : null}
          <Button
            variant="ghost"
            size="icon-sm"
            className="shrink-0 text-slate-500 hover:text-slate-800 dark:text-slate-300 dark:hover:text-slate-100"
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            aria-pressed={theme === "dark"}
            onClick={toggleTheme}
          >
            {theme === "dark" ? <Sun /> : <Moon />}
            <span className="sr-only">{theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}</span>
          </Button>
          <Dialog.Close
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                className="shrink-0 text-slate-500 hover:text-slate-800 dark:text-slate-300 dark:hover:text-slate-100"
              >
                <X />
                <span className="sr-only">Close</span>
              </Button>
            }
          />
        </div>
      </Dialog.Header>

      <Dialog.Content className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 lg:grid-cols-[24rem_minmax(0,1fr)_24rem]">
        <div className="-mx-1 min-h-0 overflow-y-auto px-1">
          <div className="flex flex-col gap-3 pt-1 pb-4">
            <QaLenses
              studioReadOnly={studioReadOnly}
              canFlag={canFlag}
              activeSpotlight={activeSpotlight}
              onAddFlag={addFlag}
              onSpotlightChange={setSpotlight}
            />

            <QaFlaggedPanel
              displayedPriorIssues={displayedPriorIssues}
              flags={flags}
              viewingPast={viewingPast}
              studioReadOnly={studioReadOnly}
              canFlag={canFlag}
              lastFailIssues={lastFailIssues}
              lastFailResolutions={lastFailResolutions}
              priorTimestampOverrides={priorTimestampOverrides}
              priorCommentOverrides={priorCommentOverrides}
              durationSeconds={durationSeconds}
              selectedFlagId={selectedFlagId}
              selectedLastFailId={selectedLastFailId}
              highlightUnmarkedLastFail={highlightUnmarkedLastFail}
              firstUnmarkedLastFailRef={firstUnmarkedLastFailRef}
              autoFocusComment={autoFocusComment}
              onSelectLastFail={(issue, timestampMs) => {
                selectLastFail(issue.id);
                focusIssue({ timestampMs });
              }}
              onSelectFlag={flag => {
                selectFlag(flag.id);
                focusIssue(flag);
              }}
              onChangeLastFailTimestamp={(id, nextMs) => {
                if (viewingPast) return;
                selectLastFail(id);
                updateLastFailTimestamp(id, nextMs);
              }}
              onChangeFlagTimestamp={(id, nextMs) => {
                selectFlag(id);
                updateFlagTimestamp(id, nextMs);
              }}
              onUsePlayheadLastFail={id => {
                if (viewingPast) return;
                selectLastFail(id);
                updateLastFailTimestamp(id, playheadTimestampMs, false);
              }}
              onUsePlayheadFlag={id => {
                selectFlag(id);
                updateFlagTimestamp(id, playheadTimestampMs, false);
              }}
              onChangeLastFailComment={(id, nextComment) => {
                if (viewingPast) return;
                setLastFailComments(current => ({ ...current, [id]: nextComment }));
              }}
              onChangeFlagComment={(id, nextComment) =>
                setFlags(current => current.map(item => (item.id === id ? { ...item, comment: nextComment } : item)))
              }
              onDeselectLastFail={() => selectLastFail(null)}
              onDeselectFlag={() => selectFlag(null)}
              onResolved={id => setLastFailResolutions(current => ({ ...current, [id]: "RESOLVED" }))}
              onUnresolved={id => setLastFailResolutions(current => ({ ...current, [id]: "UNRESOLVED" }))}
              onDeleteFlag={removeFlag}
            />
          </div>
        </div>

        <QaPlayerStage
          clips={clips}
          selectedClip={selectedClip}
          activeMedia={activeMedia}
          missingPastVideo={missingPastVideo}
          viewingPast={viewingPast}
          studioReadOnly={studioReadOnly}
          playing={playing}
          playbackRate={playbackRate}
          currentSeconds={currentSeconds}
          durationSeconds={durationSeconds}
          maxPlayed={maxPlayed}
          watchComplete={watchComplete}
          flags={flags}
          displayedPriorIssues={displayedPriorIssues}
          selectedFlagId={selectedFlagId}
          selectedLastFailId={selectedLastFailId}
          priorTimestampOverrides={priorTimestampOverrides}
          playbackCue={playbackCue}
          skipCue={skipCue}
          transcriptCollapsed={transcriptCollapsed}
          textHighlighted={isSpotlighted("text")}
          sourceHighlighted={isSpotlighted("source")}
          playerRef={playerRef}
          timelineRef={timelineRef}
          onTogglePlaying={() => setPlayingWithCue(current => !current)}
          onPlayingChange={setPlayingWithCue}
          onProgress={({ playedSeconds, played }) => {
            setCurrentSeconds(playedSeconds);
            if (selectedClip?.source === "EDITED") {
              setMaxPlayed(current => Math.max(current, played));
            }
          }}
          onDuration={handleDuration}
          onSelectClip={selectClip}
          onSeek={seekTo}
          onSkip={skipBy}
          onPlaybackRateChange={setPlaybackRate}
          onSeekLine={seekToTimedLine}
          onToggleTranscript={toggleTranscript}
          onMoveFlag={(id, clientX, rect) => {
            setPlaying(false);
            updateFlagTimestamp(id, timestampMsFromTimelineX(clientX, rect, durationSeconds));
          }}
          onMoveLastFail={(id, clientX, rect) => {
            setPlaying(false);
            updateLastFailTimestamp(id, timestampMsFromTimelineX(clientX, rect, durationSeconds));
          }}
        />
        <QaContextRail
          isSpotlighted={isSpotlighted}
          hasPlayerInfo={hasPlayerInfo}
          playerName={playerName}
          playerAliases={playerAliases}
          companyName={videoProject.companyName}
          regionsAndBranches={regionsAndBranches}
          player={player}
          caption={caption}
          captionBlockedWords={captionBlockedWords}
          preferencesInfo={preferencesInfo}
          formatHexColor={formatHexColor}
          playerNotes={playerNotes}
          coachNotes={coachNotes}
          videoProjectMessages={videoProjectMessages}
        />
      </Dialog.Content>

      <Dialog.Footer className="shrink-0 sm:items-center sm:justify-between">
        <button
          type="button"
          title="Keyboard shortcuts (?)"
          className="hidden items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 sm:flex dark:text-slate-400 dark:hover:text-slate-100"
          onClick={() => setShowShortcuts(current => !current)}
        >
          <Keyboard className="size-3.5" />
          <Kbd>?</Kbd>
        </button>
        {studioReadOnly ? (
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {viewingPast
              ? "Viewing a past QA audit — flagging and decisions are disabled."
              : "View only — flagging and decisions are disabled until QA review is in progress."}
          </p>
        ) : (
          <Button
            variant={watchComplete ? "default" : "outline"}
            title={hasIssues ? `Send back for edits (${modKey}+Enter)` : `Send to client (${modKey}+Enter)`}
            onClick={() => void handleSubmit()}
            disabled={saving}
            loading={saving}
          >
            {hasIssues ? <RotateCcw /> : <CircleCheck />}
            {hasIssues ? "Send back for edits" : "Send to client"}
            <KbdGroup>
              <Kbd>{modKey}</Kbd>
              <Kbd>Enter</Kbd>
            </KbdGroup>
          </Button>
        )}
      </Dialog.Footer>

      {showShortcuts ? (
        <ShortcutsOverlay readOnly={studioReadOnly} modKey={modKey} onClose={() => setShowShortcuts(false)} />
      ) : null}
    </Dialog>
  );
}
